import React, { useState, FormEvent } from 'react';
import { useAuth } from '@shootout-comps/core';
import { useLang } from '@shootout-comps/i18n';
import { LANG_OPTIONS } from '@shootout-comps/i18n';
import type { LangCode } from '@shootout-comps/i18n';
import { SnookerBallSpinner } from '../components/SnookerBall';
import OAuthButtons from '../components/OAuthButtons';
import PasswordInput from '../components/PasswordInput';

interface AppLoginPageProps {
  onSignUp?: () => void;
  onLoginError?: (err: Error & { reason?: string; email?: string }) => void;
  onForgotPassword?: () => void;
}

export default function AppLoginPage({ onSignUp, onLoginError, onForgotPassword }: AppLoginPageProps) {
  const { login } = useAuth();
  const { t, lang, setLang } = useLang();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError]       = useState('');
  const [loading, setLoading]   = useState(false);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setLoading(true); setError('');
    try { await login(username.trim(), password); }
    catch (err) {
      if (err instanceof Error && (err.message === 'PENDING_VERIFICATION' || err.message === 'ACCOUNT_SUSPENDED' || err.message === 'ACCOUNT_DEACTIVATED') && onLoginError) {
        onLoginError(err as Error & { reason?: string; email?: string });
      } else if (err instanceof Error && err.message === 'NO_PASSWORD_SET') {
        setError(t.noPasswordSetError);
      } else {
        setError(err instanceof Error ? err.message : 'Login failed');
      }
    }
    finally { setLoading(false); }
  };

  return (
    <div style={{
      minHeight: 'calc(100dvh - 0px)',
      display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center',
      background: 'var(--baize-dark)', padding: '1rem',
    }}>
      {/* Language selector */}
      <div style={{ marginBottom: '1.5rem', display: 'flex', gap: '0.4rem', flexWrap: 'wrap', justifyContent: 'center' }}>
        {LANG_OPTIONS.map(({ code, flag }) => (
          <button key={code} onClick={() => setLang(code as LangCode)}
            style={{
              background: code === lang ? 'rgba(201,168,76,0.2)' : 'rgba(255,255,255,0.05)',
              border: `1px solid ${code === lang ? 'rgba(201,168,76,0.5)' : 'rgba(255,255,255,0.1)'}`,
              borderRadius: '4px', padding: '0.3rem 0.5rem', fontSize: '1.1rem',
              cursor: 'pointer', minHeight: '36px', lineHeight: 1,
            }}>
            {flag}
          </button>
        ))}
      </div>

      <div style={{
        background: 'linear-gradient(135deg, var(--baize) 0%, var(--felt) 100%)',
        border: '2px solid rgba(201,168,76,0.4)',
        borderRadius: '16px', padding: 'clamp(1.5rem, 6vw, 3rem)',
        width: '100%', maxWidth: '400px',
        boxShadow: '0 24px 64px rgba(0,0,0,0.6)',
      }}>
        <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
          <SnookerBallSpinner size={40} />
          <h1 style={{ fontFamily: 'Bebas Neue', fontSize: 'clamp(1.6rem, 6vw, 2.2rem)', color: 'var(--gold)', lineHeight: 1 }}>
            {t.appName}
          </h1>
          <p style={{ color: 'var(--chalk-dim)', fontSize: '0.82rem', marginTop: '0.4rem' }}>
            {t.signIn}
          </p>
        </div>

        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '0.85rem' }}>
          {[
            { label: t.username, value: username, set: setUsername, type: 'text', auto: 'username' },
            { label: t.password, value: password, set: setPassword, type: 'password', auto: 'current-password' },
          ].map(({ label, value, set, type, auto }) => (
            <div key={label}>
              <label style={{ fontSize: '0.72rem', color: 'var(--chalk-dim)', textTransform: 'uppercase', letterSpacing: '0.1em', display: 'block', marginBottom: '0.35rem' }}>
                {label}
              </label>
              {type === 'password' ? (
                <PasswordInput
                  value={value} onChange={(e) => set(e.target.value)}
                  autoComplete={auto}
                  style={{ padding: '0.75rem 1rem', background: 'rgba(0,0,0,0.3)', border: '1.5px solid rgba(201,168,76,0.3)', borderRadius: '8px', color: 'var(--chalk)', outline: 'none' }}
                />
              ) : (
                <input
                  type={type} value={value} onChange={(e) => set(e.target.value)}
                  autoComplete={auto}
                  style={{ width: '100%', padding: '0.75rem 1rem', background: 'rgba(0,0,0,0.3)', border: '1.5px solid rgba(201,168,76,0.3)', borderRadius: '8px', color: 'var(--chalk)', outline: 'none' }}
                />
              )}
            </div>
          ))}

          {error && (
            <div style={{ background: 'rgba(192,57,43,0.2)', border: '1px solid rgba(192,57,43,0.4)', borderRadius: '6px', padding: '0.6rem 0.85rem', color: '#e74c3c', fontSize: '0.82rem' }}>
              {error}
            </div>
          )}

          <button type="submit" disabled={loading || !username || !password} style={{
            marginTop: '0.5rem', background: 'linear-gradient(135deg, var(--gold), var(--gold-light))',
            color: 'var(--baize-dark)', border: 'none', borderRadius: '8px',
            padding: '0.9rem', fontSize: '1rem', fontWeight: 700,
            letterSpacing: '0.08em', textTransform: 'uppercase', cursor: 'pointer',
            opacity: loading || !username || !password ? 0.55 : 1,
          }}>
            {loading ? t.signingIn : t.signIn}
          </button>

          {onForgotPassword && (
            <div style={{ textAlign: 'center', marginTop: '0.25rem' }}>
              <button
                type="button"
                onClick={onForgotPassword}
                style={{ background: 'none', border: 'none', color: 'var(--chalk-dim)', cursor: 'pointer', fontSize: '0.8rem', textDecoration: 'underline', padding: '0.25rem' }}
              >
                {t.forgotPassword}
              </button>
            </div>
          )}
        </form>

        <div style={{ marginTop: '1.25rem' }}>
          <OAuthButtons />
        </div>
      </div>
      {onSignUp && (
        <p style={{ textAlign: 'center', color: 'var(--chalk-dim)', fontSize: '0.82rem', marginTop: '1.25rem' }}>
          {t.dontHaveAccount}{' '}
          <button onClick={onSignUp} style={{ background: 'none', border: 'none', color: 'var(--gold)', cursor: 'pointer', fontWeight: 600, fontSize: '0.82rem', textDecoration: 'underline' }}>
            {t.createFreeAccount}
          </button>
        </p>
      )}
    </div>
  );
}
